import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Typography, Paper, IconButton, Select, MenuItem, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';

const openCodeInVSCode = async (codeSnippet: string, language: string = 'javascript') => {
    const AUTH_TOKEN = "jIVxNFGzeImEYxK2vjMKCP2m349bqthuDTagDZadqZ87qLwozmaXjUn";

    try {
        const response = await fetch('http://127.0.0.1:4842/open', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AUTH_TOKEN}`
            },
            body: JSON.stringify({
                content: codeSnippet,
                language: language
            })
        });

        if (!response.ok) {
            throw new Error("Erreur d'authentification avec l'extension");
        }
    } catch (error) {
        console.error(error);
        alert("Impossible de communiquer avec Edu-JALM Helper.");
    }
};

interface CodeTesterProps {
    initialCode: string;
    language: string;
    onCodeChange?: (code: string) => void;
    onLanguageChange?: (language: string) => void;
    readOnly?: boolean; // If true, maybe just show code? But the point is to test.
    mode?: 'builder' | 'learner'; // Builder might want to set the "Initial Code". Learner wants to reset to "Initial Code".
}

const CodeTester: React.FC<CodeTesterProps> = ({ initialCode, language, onCodeChange, onLanguageChange, mode = 'learner' }) => {
    const [code, setCode] = useState(initialCode);
    const [output, setOutput] = useState('');

    // Update local state if prop changes (e.g. from DB in builder)
    useEffect(() => {
        setCode(initialCode);
    }, [initialCode]);

    const handleCodeChange = (newCode: string) => {
        setCode(newCode);
        if (onCodeChange) {
            onCodeChange(newCode);
        }
    };

    const runCode = () => {
        // For HTML/JS, we inject into iframe
        if (language === 'html' || language === 'javascript') {
            const htmlContent = language === 'javascript'
                ? `<html><body><script>${code}<\/script></body></html>`
                : code;
            setOutput(htmlContent);
        } else {
            // For other languages, we might just show a message or use a mock console
            setOutput(`Execution for ${language} is not supported in this preview yet.`);
        }
    };

    // Auto-run on load or debounce? Maybe manual run is better to avoid flashing.
    // Let's do manual run for now, or auto-run if it's simple HTML.
    useEffect(() => {
        const timer = setTimeout(() => {
            runCode();
        }, 1000);
        return () => clearTimeout(timer);
    }, [code, language]);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            height: '500px',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#fff'
        }}>
            {/* Code Editor Section */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: { md: '1px solid #e0e0e0' }, borderBottom: { xs: '1px solid #e0e0e0', md: 'none' } }}>
                <Box sx={{
                    p: 1,
                    bgcolor: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>Code Editor</Typography>
                    {mode === 'builder' && onLanguageChange && (
                        <Select
                            value={language}
                            onChange={(e) => onLanguageChange(e.target.value)}
                            size="small"
                            variant="standard"
                            disableUnderline
                            sx={{ fontSize: '0.875rem', color: '#444' }}
                        >
                            <MenuItem value="html">HTML</MenuItem>
                            <MenuItem value="javascript">JavaScript</MenuItem>
                            <MenuItem value="css">CSS</MenuItem>
                        </Select>
                    )}
                    {mode === 'learner' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Ouvrir avec Visual Studio Code" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => openCodeInVSCode(code, language)}
                                    sx={{
                                        color: '#666',
                                        '&:hover': {
                                            color: '#0078d4',
                                            bgcolor: 'rgba(0, 120, 212, 0.08)'
                                        },
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.583 2.248L12.23 6.6l-4.088-3.104L2.5 5.81v12.38l5.643 2.314L12.23 17.4l5.353 4.352L23.5 19.065V4.935l-5.917-2.687zM8.143 16.676V7.324l9.714 4.676-9.714 4.676z" />
                                    </svg>
                                </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={() => setCode(initialCode)} title="Reset Code">
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
                <TextField
                    fullWidth
                    multiline
                    value={code}
                    onChange={(e) => handleCodeChange(e.target.value)}
                    variant="standard"
                    InputProps={{
                        disableUnderline: true,
                        sx: {
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            p: 2,
                            height: '100%',
                            alignItems: 'flex-start',
                            overflowY: 'auto'
                        }
                    }}
                    sx={{ flex: 1, overflow: 'hidden', '& .MuiInputBase-root': { height: '100%' } }}
                    placeholder="// Write your code here..."
                />
            </Box>

            {/* Preview Section */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fafafa' }}>
                <Box sx={{
                    p: 1,
                    bgcolor: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>Live Preview</Typography>
                    <IconButton size="small" onClick={runCode} color="primary" title="Run Code">
                        <PlayArrowIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box sx={{ flex: 1, position: 'relative' }}>
                    <iframe
                        title="preview"
                        srcDoc={output}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            backgroundColor: '#fff'
                        }}
                        sandbox="allow-scripts"
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default CodeTester;
